import { onlineManager } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';
import { storage } from '~/lib/storage';

/**
 * Online state = browser connectivity AND not forced offline from Me → "Offline mode".
 * Forcing offline lets staff queue check-ins on purpose at a venue with flaky signal.
 */
let forced = false;
const forcedListeners = new Set<() => void>();

export function initOnline() {
  forced = storage.get('forceOffline') === '1';
  onlineManager.setEventListener((setOnline) => {
    const update = () => setOnline(navigator.onLine && !forced);
    update();
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  });
}

export function setForcedOffline(value: boolean) {
  forced = value;
  storage.set('forceOffline', value ? '1' : null);
  for (const l of forcedListeners) l();
  onlineManager.setOnline(navigator.onLine && !value);
}

export const isForcedOffline = () => forced;

export function useOnline() {
  return useSyncExternalStore(
    (l) => onlineManager.subscribe(l),
    () => onlineManager.isOnline(),
  );
}

export function useForcedOffline() {
  return useSyncExternalStore(
    (l) => {
      forcedListeners.add(l);
      return () => forcedListeners.delete(l);
    },
    () => forced,
  );
}
